import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IEmployer } from "../../types";
import { LuDownload, LuEllipsisVertical } from "react-icons/lu";
import { saveAs } from "file-saver";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

import { Table } from "../../components/table/table";
import { fetchEmployers } from "../../services/employerService";

const LuDownloadIcon = LuDownload as React.ElementType;
const LuEllipsisVerticalIcon = LuEllipsisVertical as React.ElementType;

interface EmployersProps {
  searchQuery?: string;
}

const Employers: React.FC<EmployersProps> = ({ searchQuery }) => {
  const navigate = useNavigate();

  const {
    data: employers,
    isLoading,
    error,
  } = useQuery<IEmployer[]>({
    queryKey: ["employers", searchQuery],
    queryFn: () => fetchEmployers(searchQuery),
  });

  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

  const handleView = (id: string) => {
    setDropdownOpen(null);
    navigate(`/users/${id}`, { state: { referrer: "/users/employers" } });
  };

  const toggleDropdown = (id: string) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".relative")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const downloadCSV = () => {
    if (!employers || employers.length === 0) return;

    const headers = [
      "Company",
      "Specialization",
      "Website",
      "Registration No",
      "Status",
    ];
    const rows = employers.map((emp) => [
      `"${emp.companyName}"`,
      `"${emp.areaOfSpecialization}"`,
      `"${emp.website || "N/A"}"`,
      `"${emp.registrationNo}"`,
      `"${emp.user.userStatus}"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "employers.csv");
  };

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );
  if (error)
    return <p className="text-rose-500 text-sm">Error loading employers.</p>;

  const columns = [
    {
      header: "Company",
      accessorKey: "companyName",
      cell: ({ row }: { row: { original: IEmployer } }) => (
        <div className="flex items-center space-x-2">
          <img
            src={row.original.logo || "/default-logo.png"}
            alt="Company Logo"
            className="w-8 h-8 rounded-full"
          />
          <div>
            <p className="font-medium text-gray-800">
              {row.original.companyName || "N/A"}
            </p>
            <p className="text-sm text-gray-500">
              {row.original.notificationEmail || (
                <span className="italic">Not added</span>
              )}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Specialization",
      accessorKey: "areaOfSpecialization",
      cell: ({ row }: { row: { original: IEmployer } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.areaOfSpecialization || (
            <span className="italic">Not added</span>
          )}
        </p>
      ),
    },
    {
      header: "Website",
      accessorKey: "website",
      cell: ({ row }: { row: { original: IEmployer } }) => (
        <a
          href={row.original.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-500 hover:underline"
        >
          {row.original.website || "N/A"}
        </a>
      ),
    },
    {
      header: "Registration No",
      accessorKey: "registrationNo",
      cell: ({ row }: { row: { original: IEmployer } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.registrationNo || (
            <span className="italic">Not added</span>
          )}
        </p>
      ),
    },
    {
      header: "Status",
      accessorKey: "user.status",
      cell: ({ row }: { row: { original: IEmployer } }) => {
        return (
          <span
            className={`px-2 py-1 text-sm capitalize ${
              row.original.user.userStatus === "activated"
                ? "text-emerald-500"
                : row.original.user.userStatus === "suspended"
                ? "text-rose-500"
                : "text-amber-500"
            }`}
          >
            {row.original.user.userStatus}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IEmployer } }) => (
        <div className="relative">
          <button
            onClick={() => toggleDropdown(row.original.user._id)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <LuEllipsisVerticalIcon className="text-gray-600 text-lg" />
          </button>

          {dropdownOpen === row.original.user._id && (
            <div
              className="absolute right-0 mt-2 w-24 bg-white shadow-md border rounded-md z-10"
              onClick={() => handleView(row.original.user._id)}
            >
              <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">
                View
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Employers</h2>
        <div className="flex items-center gap-2">
          <div
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center justify-between space-x-2 text-gray-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </div>
        </div>
      </div>
      <Table columns={columns} data={employers || []} />
    </div>
  );
};

export default Employers;
